import React, { useState, useEffect } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { toast } from "sonner"
import Breadcrumb from "components/Common/Breadcrumb"
import DashboardLoader from "components/Loader/DashboardLoader"
import { recipeApiService } from "services/recipeApiService"

const CreateRecipePage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditMode = !!id

  const [isLoading, setIsLoading] = useState(isEditMode)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [ingredients, setIngredients] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [showSearchResults, setShowSearchResults] = useState(false)

  // Initialize recipe state
  const [recipe, setRecipe] = useState({
    recipe_name: "",
    ingredient_details: [],
    subtotal: 0,
    q_factor: 3,
    recipe_cost: 0,
    portion_total: "",
    portion_unit: 0,
    menu_price: "",
    note: "",
    item_image: null,
  })

  // Fetch recipe data if in edit mode
  useEffect(() => {
    const fetchRecipeData = async () => {
      if (!isEditMode) return

      setIsLoading(true)
      try {
        const data = await recipeApiService.getRecipe(id)
        setRecipe({
          ...data,
          ingredient_details: data.ingredient_details || [],
          q_factor: data.q_factor || 3,
        })
      } catch (error) {
        toast.error("Failed to fetch recipe data")
        console.error("Error fetching recipe:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchRecipeData()
  }, [id, isEditMode])

  // Fetch all ingredients
  useEffect(() => {
    const fetchIngredients = async () => {
      try {
        // Note: You'll need to create an ingredientApiService similar to recipeApiService
        const response = await fetch(
          "http://127.0.0.1:8000/ingredientCost/",
        )
        const data = await response.json()
        setIngredients(data)
      } catch (error) {
        console.error("Error fetching ingredients:", error)
      }
    }

    fetchIngredients()
  }, [])

  // Handle search for ingredients
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setSearchResults([])
      return
    }

    const results = ingredients.filter(ingredient =>
      ingredient.ingredient_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    )
    setSearchResults(results)
  }, [searchTerm, ingredients])

  // Calculate recipe costs whenever ingredient details or q_factor changes
  useEffect(() => {
    const subtotal = recipe.ingredient_details.reduce(
      (sum, item) => sum + (parseFloat(item.ingredient_cost) || 0),
      0,
    )

    const qFactor = parseFloat(recipe.q_factor) || 0
    const qFactorAmount = subtotal * (qFactor / 100)
    const recipeCost = subtotal + qFactorAmount

    const portionTotal = parseFloat(recipe.portion_total)
    const portionUnit = portionTotal ? recipeCost / portionTotal : 0

    setRecipe(prev => ({
      ...prev,
      subtotal: subtotal.toFixed(2),
      recipe_cost: recipeCost.toFixed(2),
      portion_unit: portionUnit.toFixed(2),
    }))
  }, [recipe.ingredient_details, recipe.q_factor, recipe.portion_total])

  // Handlers
  const handleInputChange = (field, value) => {
    setRecipe(prev => ({ ...prev, [field]: value }))
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
    setShowSearchResults(true)
  }

  const handleIngredientSelect = ingredient => {
    const newIngredient = {
      id: ingredient.id,
      ingredient_name: ingredient.ingredient_name,
      unit: ingredient.unit,
      unitprice: parseFloat(ingredient.unitprice),
      recipe_quantity: "",
      ingredient_cost: 0,
      store_name: ingredient.store_name,
    }

    setRecipe(prev => ({
      ...prev,
      ingredient_details: [...prev.ingredient_details, newIngredient],
    }))

    setSearchTerm("")
    setShowSearchResults(false)
  }

  const handleIngredientQuantityChange = (index, value) => {
    const updatedIngredients = [...recipe.ingredient_details]
    const quantity = parseFloat(value) || 0
    const unitprice = parseFloat(updatedIngredients[index].unitprice) || 0
    const ingredient_cost = parseFloat((quantity * unitprice).toFixed(2))

    updatedIngredients[index].recipe_quantity = value
    updatedIngredients[index].ingredient_cost = ingredient_cost

    setRecipe(prev => ({
      ...prev,
      ingredient_details: updatedIngredients,
    }))
  }

  const handleRemoveIngredient = index => {
    const updatedIngredients = [...recipe.ingredient_details]
    updatedIngredients.splice(index, 1)
    setRecipe(prev => ({
      ...prev,
      ingredient_details: updatedIngredients,
    }))
  }

  const handleImageChange = e => {
    const file = e.target.files[0]
    setRecipe(prev => ({ ...prev, item_image: file }))
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Format ingredient details
      const formattedIngredients = recipe.ingredient_details.map(item => ({
        ...item,
        ingredient_cost: Number(item.ingredient_cost).toFixed(2),
      }))

      // Prepare recipe data
      const recipeData = {
        recipe_name: recipe.recipe_name,
        ingredient_details: formattedIngredients,
        subtotal: Number(recipe.subtotal).toFixed(2),
        q_factor: recipe.q_factor,
        recipe_cost: Number(recipe.recipe_cost).toFixed(2),
        portion_total: recipe.portion_total,
        portion_unit: Number(recipe.portion_unit).toFixed(2),
        menu_price: recipe.menu_price,
        note: recipe.note,
      }

      if (isEditMode) {
        await recipeApiService.updateRecipe(id, recipeData)
        toast.success("Recipe updated successfully")
      } else {
        await recipeApiService.createRecipe(recipeData)
        toast.success("Recipe created successfully")
      }

      // Navigate back to recipes list after delay
      setTimeout(() => {
        navigate("/recipes")
      }, 1500)
    } catch (error) {
      console.error("Error saving recipe:", error)
      toast.error(`Failed to ${isEditMode ? "update" : "create"} recipe`)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return <DashboardLoader />
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb
          title="Recipes"
          breadcrumbItem={isEditMode ? "Update Recipe" : "Create Recipe"}
        />

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title mb-4">
                  {isEditMode ? "Update Recipe" : "Create New Recipe"}
                </h4>

                <form onSubmit={handleSubmit}>
                  {/* Recipe Basic Info */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Recipe Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={recipe.recipe_name}
                          onChange={e =>
                            handleInputChange("recipe_name", e.target.value)
                          }
                          required
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Item Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={handleImageChange}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Note Field */}
                  <div className="mb-4">
                    <label className="form-label">Note</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={recipe.note}
                      onChange={e => handleInputChange("note", e.target.value)}
                    />
                  </div>

                  {/* Ingredients Section */}
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Ingredients</h5>

                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowSearchResults(true)}
                      >
                        <i className="fas fa-plus me-1"></i> Add Ingredient
                      </button>
                    </div>

                    {/* Search Ingredients */}
                    {showSearchResults && (
                      <div className="mb-3">
                        <label className="form-label">Search Ingredient</label>
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            placeholder="Type ingredient name..."
                          />

                          {searchResults.length > 0 && (
                            <div
                              className="position-absolute w-100 bg-white border rounded shadow-lg mt-1 z-1"
                              style={{ maxHeight: "200px", overflowY: "auto" }}
                            >
                              {searchResults.map(ingredient => (
                                <div
                                  key={ingredient.id}
                                  className="p-2 border-bottom hover-bg-light cursor-pointer"
                                  onClick={() =>
                                    handleIngredientSelect(ingredient)
                                  }
                                >
                                  <div className="d-flex justify-content-between">
                                    {ingredient.ingredient_name} -{" "}
                                    {ingredient.unitprice}/{ingredient.unit}
                                    {/* <span>{ingredient.ingredient_name}</span>
                                    <small className="text-muted">
                                      ৳{ingredient.unitprice}/{ingredient.unit}
                                    </small> */}
                                  </div>
                                  {/* <small className="text-muted">{ingredient.store_name}</small> */}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Ingredients Table */}
                    {recipe.ingredient_details.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Ingredient</th>
                              <th>Recipe Quantity</th>
                              <th>Unit</th>
                              <th>Unit Cost</th>
                              <th>Ingredient Cost</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recipe.ingredient_details.map(
                              (ingredient, index) => (
                                <tr key={index}>
                                  <td>
                                    <div>
                                      <strong>
                                        {ingredient.ingredient_name}
                                      </strong>
                                    </div>
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={ingredient.recipe_quantity}
                                      onChange={e =>
                                        handleIngredientQuantityChange(
                                          index,
                                          e.target.value,
                                        )
                                      }
                                      required
                                      step="0.01"
                                      min="0"
                                    />
                                  </td>
                                  <td>{ingredient.unit}</td>
                                  <td>${ingredient.unitprice.toFixed(2)}</td>
                                  <td>
                                    <span className="fw-semibold">
                                      ${ingredient.ingredient_cost || "0.00"}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm"
                                      onClick={() =>
                                        handleRemoveIngredient(index)
                                      }
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="alert alert-info text-center">
                        No ingredients added. Click "Add Ingredient" to add
                        ingredients.
                      </div>
                    )}
                  </div>

                  {/* Cost Calculations */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title mb-3">Cost Calculation</h5>

                          <div className="mb-3">
                            <label className="form-label">Subtotal</label>
                            <input
                              type="text"
                              className="form-control bg-light"
                              value={`$${recipe.subtotal}`}
                              readOnly
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Q Factor (%)</label>
                            <input
                              type="number"
                              className="form-control"
                              value={recipe.q_factor}
                              onChange={e =>
                                handleInputChange("q_factor", e.target.value)
                              }
                              step="0.01"
                              min="0"
                            />
                            <small className="text-muted">
                              Additional cost percentage added to subtotal
                            </small>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Recipe Cost</label>
                            <input
                              type="text"
                              className="form-control bg-light fw-bold"
                              value={`$${recipe.recipe_cost}`}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title mb-3">Portion & Pricing</h5>

                          <div className="mb-3">
                            <label className="form-label">Portion Total</label>
                            <input
                              type="number"
                              className="form-control"
                              value={recipe.portion_total}
                              onChange={e =>
                                handleInputChange(
                                  "portion_total",
                                  e.target.value,
                                )
                              }
                              step="0.01"
                              min="0"
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Portion/Unit</label>
                            <input
                              type="text"
                              className="form-control bg-light"
                              value={`$${recipe.portion_unit}`}
                              readOnly
                            />
                            <small className="text-muted">
                              Cost per portion
                            </small>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">
                              Menu Price (Optional)
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={recipe.menu_price}
                              onChange={e =>
                                handleInputChange("menu_price", e.target.value)
                              }
                              step="0.01"
                              min="0"
                            />
                            <small className="text-muted">
                              Selling price for menu
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="row mt-4">
                    <div className="col-12">
                      <div className="d-flex ms-2">
                        {/* <Link to="/recipes" className="btn btn-light">
                          <i className="fas fa-arrow-left me-1"></i> Back to List
                        </Link> */}

                        <button
                          type="submit"
                          className={`btn ${isEditMode ? "btn-warning" : "btn-success"}`}
                          disabled={
                            isSubmitting ||
                            recipe.ingredient_details.length === 0
                          }
                        >
                          {isSubmitting ? (
                            <>
                              <i className="fas fa-spinner fa-spin me-1"></i>
                              Processing...
                            </>
                          ) : isEditMode ? (
                            <>
                              <i className="fas fa-save me-1"></i>
                              Update Recipe
                            </>
                          ) : (
                            <>
                              <i className="fas fa-plus me-1"></i>
                              Create Recipe
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateRecipePage
